import { CardHeader } from "@mui/material";
import { useEffect, useState } from "react";
import shortid from "shortid";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  Dialog,
  DialogContent,
  Grid,
} from "../../../../../components";
import {
  notification,
  TefHubService,
  TefService,
} from "../../../../../services";
import {
  formatPrecisaoFloat,
  parcelaData,
  responseErros,
} from "../../../../../utils";
import AdicionarPagamentoDialogContent from "./adicionar-pagamento-dialog-content.component";
import { DATATYPE_TEF } from "../../../../../resources";
import { stringFinalizacaoRotinaTef, stringsErroMensagemTef } from "../../../../../resources/constantes";
import notificationService from "../../../../../services/notification.service";

const tefHubService = new TefHubService();

const AdicionarPagamentoDialog = ({
  open,
  setOpen,
  pagamentoSelecionado,
  setPagamentoSelecionado,
  meioPagamentoList,
  condicaoPagamentoList,
  condicaoSelecionada,
  setCondicaoSelecionada,
  listaPagamentosVenda,
  setListaPagamentosVenda,
  venda,
  setVenda,
  pdvEntity,
  setLoadingTef,
  setOpenDialogMenuTef,
  setDadosMenuTef,
  setTefConnection,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [condicoesFiltradas, setCondicoesFiltradas] = useState([]);
  const [valorPagamento, setValorPagamento] = useState(0);
  const [numeroAutorizacao, setNumeroAutorizacao] = useState(null);
  const [inputError, setInputError] = useState(null);

  useEffect(() => {
    if (open) {
      if (venda?.totalVenda !== venda?.totalPagamentos) {
        setValorPagamento(
          parseFloat(venda?.totalVenda ?? 0) -
            parseFloat(venda?.totalPagamentos ?? 0),
        );
      }
      if (pagamentoSelecionado) {
        const condicoes = filtrarCondicoesPagamento(pagamentoSelecionado);
        setCondicoesFiltradas(condicoes);
        setActiveStep(1);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const filtrarCondicoesPagamento = (pagamentoSelecionado) => {
    const condicaoPagamentoFiltrado = [];
    condicaoPagamentoList.forEach((condicao) =>
      // eslint-disable-next-line array-callback-return
      condicao.meiosPagamentos.filter((meio) => {
        if (meio.id === pagamentoSelecionado?.id) {
          condicaoPagamentoFiltrado.push(condicao);
        }
      }),
    );
    if (condicaoPagamentoFiltrado.length > 0) {
      return condicaoPagamentoFiltrado;
    } else {
      return [];
    }
  };

  const handleCloseDialog = () => {
    handleInitPagamentos();
    setOpen(false);
  };

  const handleInitPagamentos = () => {
    setActiveStep(0);
    setPagamentoSelecionado(null);
    setCondicaoSelecionada(null);
    setCondicoesFiltradas([]);
  };

  const onSubmitParcelas = (event) => {
    event.preventDefault();
    handleGerarParcelas();
  };

  function validarParcela() {
    if (!pagamentoSelecionado?.id) {
      notification.erroValidacao("Meio de pagamento");
      return true;
    }
    if (!condicaoSelecionada?.id) {
      notification.erroValidacao("Condição de pagamento");
      return true;
    }
    if (!condicaoSelecionada?.parcelas) {
      notification.erroValidacao("Quantidade de Parcelas");
      return true;
    }
    if (!valorPagamento) {
      setInputError("valorPagamentoPdv");
      notification.erroValidacao("Valor do título");
      return true;
    }
    if (
      pagamentoSelecionado?.cartaoCreditoDebito &&
      !pagamentoSelecionado?.tefIntegrado &&
      !numeroAutorizacao
    ) {
      setInputError("numeroAutorizacao");
      notification.erroValidacao("Número de Autorização");
      return true;
    }
    if (
      parseFloat(pdvEntity.totalPagamentos || 0) + parseFloat(valorPagamento) >
      pdvEntity.totalVenda
    ) {
      if (pagamentoSelecionado?.nfePagamento === "fpDinheiro") return;
      notification.alertaGenericos(
        "Valor inserido ultrapassa total da Nota fiscal",
      );
      return true;
    }
    return false;
  }

  const gerarParcelas = (nsu) => {
    const valorParcela = valorPagamento / condicaoSelecionada.parcelas;
    const valorFormatado = formatPrecisaoFloat(valorParcela);
    const restoDivisao =
      valorPagamento - valorFormatado * condicaoSelecionada.parcelas;
    const parcelasGeradas = [...listaPagamentosVenda];
    for (
      let contador = 1;
      contador <= condicaoSelecionada?.parcelas;
      contador++
    ) {
      const valorTitulo =
        contador === condicaoSelecionada.parcelas
          ? valorFormatado + parseFloat(restoDivisao.toFixed(2))
          : valorFormatado;
      const parcelaGerada = {
        id: shortid.generate(),
        numeroTitulo: listaPagamentosVenda.length
          ? listaPagamentosVenda.length + contador
          : contador,
        condicaoPagamentoId: condicaoSelecionada?.id,
        numeroAutorizacao: nsu ?? numeroAutorizacao,
        meioPagamentoId: pagamentoSelecionado?.id,
        meioPagamentoSelecionado: pagamentoSelecionado,
        valorTitulo,
        dataVencimento: parcelaData(
          contador,
          new Date(),
          condicaoSelecionada?.periodo,
        ),
      };
      parcelasGeradas.push(parcelaGerada);
    }
    pdvEntity.setChangePagamentos(parcelasGeradas);
    setListaPagamentosVenda(parcelasGeradas);
    setVenda(pdvEntity);
    setValorPagamento(
      pdvEntity?.totalVenda -
        parseFloat(pdvEntity?.totalPagamentos ?? 0) -
        parseFloat(valorPagamento ?? 0),
    );
    handleCloseDialog();
    notification.sucessoGenericos("Parcelas geradas com sucesso");
  };

  const handleGerarParcelas = async () => {
    const parcelaIsNotValid = validarParcela();
    if (parcelaIsNotValid) return;

    if (pagamentoSelecionado?.tefIntegrado) {
      handleCloseDialog();
      await iniciarPagamentoTef(pagamentoSelecionado, valorPagamento);
    } else {
      gerarParcelas();
    }
  };

  async function iniciarPagamentoTef(pagamento, valor) {
    const tefConnection = new TefService();
    setTefConnection(tefConnection);
    setLoadingTef(true);

    let finishTransaction = false;
    let parcelas = condicaoSelecionada?.parcelas ?? 1,
      condicao = parcelas === 1 ? "1" : "2",
      menuParcelas = 0;

    try {
      await tefConnection.connect();
    } catch (error) {
      setLoadingTef(false);
      notification.erroInternoServidor();
      console.log("Falhou a conexão com signalr =>", error?.toString());
      return;
    }

    let mensagemErro = null;
    tefConnection.onInicializaTef(() => {
      console.log("Inicializa Tef...");
      setOpenDialogMenuTef(true);
      setLoadingTef(false);
    });

    tefConnection.onMontaMenuTef(async (payload) => {
      console.log("Monta Menu Tef");
      console.log(payload);
      if (payload.dataType === DATATYPE_TEF.CONFIRMATION &&
          payload.mensagem.startsWith('13 -')
      ) {
        payload.mensagem = "Deseja cancelar a operação?";
      }
      if (payload.dataType !== DATATYPE_TEF.AWAIT) {
        // if (pagamento.modalidadeTef === 3) {
        //   if (payload.dataType === DATATYPE_TEF.MENU) {
        //     menuParcelas++;
        //   }
        //   if (menuParcelas === 3) {
        //     await tefHubService.callback({
        //       interromper: false,
        //       voltar: false,
        //       respostaSitef: parcelas.toString(),
        //     });
        //   }
        //   if (menuParcelas === 2) {
        //     await tefHubService.callback({
        //       interromper: false,
        //       voltar: false,
        //       respostaSitef: condicao,
        //     });
        //   }
        // }
        // if (menuParcelas <= 1) {
          setDadosMenuTef((prev) => ({
            ...prev,
            ...payload,
            itensMenu: payload?.itensMenu?.filter((item) => item !== ""),
          }));
        // }
      } else {
        notification.alertaGenericos(payload?.mensagem);
      }
    });

    tefConnection.onExibeMensagemTef((mensagem) => {
      console.log("signalr.onExibeMensagemTef", { mensagem });
      if (mensagem.toLowerCase() === stringFinalizacaoRotinaTef.toLowerCase()) {
        finishTransaction = true;
      }
      setDadosMenuTef((prev) => ({
        mensagem,
        qrCode: prev?.qrCode,
        dataType: prev?.qrCode ? prev.dataType : null,
      }));
    });

    tefConnection.onExibeQRCodeTef((payload) => {
      console.log("signalr.onExibeQRCodeTef", payload);
      setDadosMenuTef((prev) => ({
        ...payload,
        mensagem: prev.mensagem,
        qrCode: payload.mensagem,
      }));
    });

    tefConnection.onFinalizacaoTef(async (payload) => {
      console.log("signalr.onFinalizacaoTef", payload);
      if (payload?.nsu) {
        setOpenDialogMenuTef(false);
        gerarParcelas(payload?.nsu);
        const result = await tefHubService.imprimirComprovante(payload);
        if (!result.isAxiosError) {
          notification.sucessoGenericos("Impressão do comprovante concluída!");
        } else {
          responseErros(result);
        }
      }

      setDadosMenuTef(null);
      await tefConnection.disconnect();
    });

    tefConnection.onMensagemTef(async (mensagem) => {
      console.log("signalr.onMensagemTef", mensagem);
      if (
        stringsErroMensagemTef.some((item) =>
          mensagem.toLowerCase().includes(item),
        )
      ) {
        setTimeout(async () => {
          await tefConnection.disconnect();
          setOpenDialogMenuTef(false);
          setDadosMenuTef({});
          notification.erroGenericos(mensagem);
        }, 1000);
      }
    });

    tefConnection.onReconnecting((error) => {
      console.log("Conexão perdida, tentando reconectar...");
      notificationService.alertaGenericos("Tentando reconexão com o servidor");
      setLoadingTef(true);
      if (error) console.error("Motivo:", error);
    });

    const renderReconnectMessage = () => {
      setLoadingTef(false);
      setOpenDialogMenuTef(false);
      setDadosMenuTef({});

      let mensagem = "Conexão perdida! Favor refazer a transação";
      if (finishTransaction) {
        mensagem = "Transação pendente! Favor realizar nova operação";
        gerarParcelas();
        notificationService.alertaGenericos(mensagem);
      } else {
        notificationService.erroGenericos(mensagem);
      }
      
      setOpenDialogMenuTef(false);
      setDadosMenuTef({});
    }

    tefConnection.onReconnected(async () => {
      console.log("Reconectado com sucesso!");
      await tefConnection.disconnect();
      renderReconnectMessage();
    });

    tefConnection.onClose(() => {
      console.log("Conexão perdida. Tentando reconectar...");
      renderReconnectMessage();
    });

    try {
      await tefConnection.invokeInicializaTef();
    } catch (error) {
      console.error("Erro no invokeInicializaTef:", error);
      setLoadingTef(false);
      setOpenDialogMenuTef(false);
      notification.erroGenericos(error?.message ?? "Erro ao inicializar TEF");
      await tefConnection.disconnect();
    }

    await tefConnection.invokeEfetuaPagamentoTef(
      Math.floor(Math.random() * 100000).toString(), //Inserir numero do documento
      valor,
      pagamento.modalidadeTef,
      false,
    );
  }

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      PaperComponent="div"
      open={open}
      onClose={handleCloseDialog}
      aria-labelledby="form-dialog-title"
    >
      <DialogContent>
        <Card className="p-3">
          <CardHeader
            title={
              <Grid container alignItems="center">
                {activeStep === 1 && (
                  <Button
                    variant="text"
                    color="padrao"
                    onClick={handleInitPagamentos}
                  >
                    <i
                      className="ph-fill ph-arrow-left"
                      style={{ color: "#494C62", fontSize: 23 }}
                    ></i>
                  </Button>
                )}
                ADICIONAR PAGAMENTO
              </Grid>
            }
          />
          <CardContent className="p-3">
            <AdicionarPagamentoDialogContent
              activeStep={activeStep}
              setActiveStep={setActiveStep}
              meioPagamentoList={meioPagamentoList}
              condicaoPagamentoList={condicaoPagamentoList}
              pagamentoSelecionado={pagamentoSelecionado}
              setPagamentoSelecionado={setPagamentoSelecionado}
              condicoesFiltradas={condicoesFiltradas}
              setCondicoesFiltradas={setCondicoesFiltradas}
              condicaoSelecionada={condicaoSelecionada}
              setCondicaoSelecionada={setCondicaoSelecionada}
              filtrarCondicoesPagamento={filtrarCondicoesPagamento}
              valorPagamento={valorPagamento}
              setValorPagamento={setValorPagamento}
              numeroAutorizacao={numeroAutorizacao}
              setNumeroAutorizacao={setNumeroAutorizacao}
              venda={venda}
              onSubmitParcelas={onSubmitParcelas}
              inputError={inputError}
            />
          </CardContent>
          <CardActions>
            {activeStep === 1 && (
              <Button
                color="primary"
                variant="contained"
                onClick={onSubmitParcelas}
              >
                <i
                  className="ph-fill ph-check-circle"
                  style={{ fontSize: 22, marginRight: 10, color: "white" }}
                />
                Confirmar
              </Button>
            )}
            <Button
              color="inherit"
              variant="contained"
              onClick={handleCloseDialog}
            >
              <i
                className="ph-fill ph-x-circle"
                style={{ fontSize: 22, marginRight: 10 }}
              />
              Fechar
            </Button>
          </CardActions>
        </Card>
      </DialogContent>
    </Dialog>
  );
};

export default AdicionarPagamentoDialog;
